import { makeKeyValueRepository } from 'src/data/repositories/key-value/key-value-repository-factory';
import { RepositoryKey } from 'src/models/common/repository/repository-key';
import { Entity } from 'src/models/entity/entity';
import { CustomError } from 'src/helpers/error/custom-error';
import { ResponsePagination } from 'src/models/common/response/response-pagination/response-pagination';
import { Attendants } from 'src/models/attendant/attendant';
import { BaseCrudServiceImpl } from '../../common/base-crud/base-crud-service-impl';
import { ILogService } from '../../common/log/log-service';
import { IAuthService } from '../../auth/auth-service';
import { IKeyParams } from 'src/models/common/params/key-params';
import { IdStatus } from 'src/models/common/base/id';
import { makeConfigService } from '../../common/config/config-service-factory';
import {
  EntityAttendantLink,
  IAttendants,
  SyncEntityAttendantsPayload,
} from './attendants';
import { makeLoadEntityFromCentralUseCase } from 'src/usecases/central/entity/load-entity-from-central/load-entity-from-central-use-case-factory';
import { LanguageKey } from 'src/main/translate/language-key/language-key';
import {
  isEntitySharingRoot,
  resolveEntitySharingRoot,
} from 'src/helpers/entity/entity-sharing';

enum AttendantsSearchKey {
  NAME = 'name',
  PAGE = 'page',
  LIMIT = 'limit',
  SORT = 'sort',
  DIRECTION = 'direction',
  ALL = 'all',
  STATUS = 'status',
  TARGET_ENTITY_ID = 'target_entity_id',
  ONLY_LINKED = 'only_linked',
}

const ATTENDANT_ENDPOINTS = {
  base: '/attendants',
  byId: (id: string) => `/attendants/${id}`,
  entityLinks: (entityId: string) => `/attendants/links/entity/${entityId}`,
};

export class AttendantsImpl
  extends BaseCrudServiceImpl<Attendants>
  implements IAttendants
{
  private readonly keyValueRepository = makeKeyValueRepository();
  private readonly configService = makeConfigService();
  private readonly loadEntitiesUseCase = makeLoadEntityFromCentralUseCase();

  constructor(
    private attendsLogService: ILogService,
    authService: IAuthService,
  ) {
    super(
      ATTENDANT_ENDPOINTS.base,
      Attendants.fromJson,
      attendsLogService,
      authService,
    );
  }

  private async getSelectedEntity(): Promise<Entity | null> {
    const selectedEntity = await this.keyValueRepository.get(
      RepositoryKey.SELECTED_ENTITY,
    );

    return selectedEntity ? (selectedEntity as Entity) : null;
  }

  private isRootEntity(entity: Entity | null | undefined): boolean {
    return isEntitySharingRoot(
      entity,
      (taxId) => this.configService.isPrincipalEntityTaxId(taxId),
    );
  }

  private async assertRootEntityAccess(): Promise<Entity> {
    const selectedEntity = await this.getSelectedEntity();

    if (!this.isRootEntity(selectedEntity)) {
      throw CustomError.invalidContent([
        LanguageKey.ONLY_ROOT_ENTITY_CAN_MANAGE_ATTENDANTS,
      ]);
    }

    return selectedEntity as Entity;
  }

  private async resolveCreationContext(): Promise<{
    selectedEntity: Entity;
    ownerRootEntity: Entity;
  }> {
    const selectedEntity = await this.getSelectedEntity();

    if (!selectedEntity?.id) {
      throw CustomError.invalidContent([
        LanguageKey.NO_SELECTED_ENTITY_TO_CREATE_ATTENDANTS,
      ]);
    }

    const isRootSelection = this.isRootEntity(selectedEntity);
    if (isRootSelection) {
      return {
        selectedEntity,
        ownerRootEntity: selectedEntity,
      };
    }

    const entities = await this.loadEntitiesUseCase.handle();
    const ownerRootEntity = resolveEntitySharingRoot(
      entities,
      selectedEntity.id,
      (taxId) => this.configService.isPrincipalEntityTaxId(taxId),
    );

    if (!ownerRootEntity?.id) {
      throw CustomError.invalidContent([
        LanguageKey.COULD_NOT_LOCATE_CONFIGURED_ROOT_ENTITY,
      ]);
    }

    return {
      selectedEntity,
      ownerRootEntity,
    };
  }

  private async appendVisibilityParams(
    params: Record<string, string>,
  ): Promise<Record<string, string>> {
    const selectedEntity = await this.getSelectedEntity();

    if (selectedEntity?.id && !this.isRootEntity(selectedEntity)) {
      return {
        ...params,
        [AttendantsSearchKey.TARGET_ENTITY_ID]: selectedEntity.id,
        [AttendantsSearchKey.ONLY_LINKED]: 'true',
      };
    }

    return params;
  }

  override async get(id: string): Promise<Attendants> {
    try {
      await this.logService.info('AttendantsImpl.get -> start');

      const params = await this.appendVisibilityParams({});
      const result = await this.authService.http.get(
        ATTENDANT_ENDPOINTS.byId(id),
        {
          params,
        },
      );

      return Attendants.fromJson(result);
    } catch (error) {
      throw CustomError.handleError('AttendantsImpl.get -> error', error);
    } finally {
      await this.logService.info('AttendantsImpl.get -> end');
    }
  }

  override async search(
    keyword: string,
    page: number,
    limit: number,
    all?: boolean,
    status?: IdStatus,
    direction?: 'asc' | 'desc',
  ): Promise<ResponsePagination> {
    const safeKeyword = keyword?.trim() || '';
    const keys: IKeyParams<AttendantsSearchKey>[] = [];

    if (safeKeyword.length > 0) {
      keys.push({ key: AttendantsSearchKey.NAME, value: safeKeyword });
    }

    keys.push({ key: AttendantsSearchKey.PAGE, value: (page || 1).toString() });
    keys.push({
      key: AttendantsSearchKey.LIMIT,
      value: (limit || 10).toString(),
    });
    keys.push({
      key: AttendantsSearchKey.DIRECTION,
      value: direction || 'asc',
    });

    if (all) {
      keys.push({
        key: AttendantsSearchKey.ALL,
        value: 'true',
      });
    }

    if (status) {
      keys.push({
        key: AttendantsSearchKey.STATUS,
        value: status,
      });
    }

    return this.searchByParams(keys);
  }

  async searchByParams(
    keys: IKeyParams<AttendantsSearchKey>[],
  ): Promise<ResponsePagination> {
    try {
      await this.logService.info('AttendantsImpl.search -> start');

      const baseParams = keys.reduce((acc, curr) => {
        acc[curr.key] = curr.value;
        return acc;
      }, {} as any);
      const params = await this.appendVisibilityParams(baseParams);

      const queryString = new URLSearchParams(
        Object.entries(params).reduce(
          (acc, [key, value]) => {
            acc[key] = String(value);
            return acc;
          },
          {} as Record<string, string>,
        ),
      ).toString();

      const result = await this.authService.http.get(
        `${this.apiPath}?${queryString}`,
      );

      return ResponsePagination.fromJson(result);
    } catch (error) {
      throw CustomError.handleError('AttendantsImpl.search -> error', error);
    } finally {
      await this.logService.info('AttendantsImpl.search -> end');
    }
  }

  override async create(data: Attendants): Promise<string> {
    try {
      await this.logService.info('AttendantsImpl.create -> start');
      const creationContext = await this.resolveCreationContext();
      const payload = Attendants.fromJson({
        ...data,
        owner_root_entity_id:
          data?.owner_root_entity_id || creationContext.ownerRootEntity.id,
        target_entity_id: creationContext.selectedEntity.id,
      });

      const result = await this.authService.http.post(
        `${this.apiPath}`,
        payload,
      );
      return result.id;
    } catch (error) {
      throw CustomError.handleError('AttendantsImpl.create -> error', error);
    } finally {
      await this.logService.info('AttendantsImpl.create -> end');
    }
  }

  override async update(id: string, data: Attendants): Promise<void> {
    try {
      await this.logService.info('AttendantsImpl.update -> start');
      const selectedRootEntity = await this.assertRootEntityAccess();
      const payload = Attendants.fromJson({
        ...data,
        owner_root_entity_id:
          data?.owner_root_entity_id || selectedRootEntity.id,
      });

      await this.authService.http.put(ATTENDANT_ENDPOINTS.byId(id), payload);
    } catch (error) {
      throw CustomError.handleError('AttendantsImpl.update -> error', error);
    } finally {
      await this.logService.info('AttendantsImpl.update -> end');
    }
  }

  override async delete(id: string): Promise<void> {
    try {
      await this.logService.info('AttendantsImpl.delete -> start');
      await this.assertRootEntityAccess();

      await this.authService.http.delete(ATTENDANT_ENDPOINTS.byId(id));
    } catch (error) {
      throw CustomError.handleError('AttendantsImpl.delete -> error', error);
    } finally {
      await this.logService.info('AttendantsImpl.delete -> end');
    }
  }

  async listEntityLinks(entityId: string): Promise<EntityAttendantLink[]> {
    try {
      await this.logService.info('AttendantsImpl.listEntityLinks -> start');
      await this.assertRootEntityAccess();

      const result = await this.authService.http.get(
        ATTENDANT_ENDPOINTS.entityLinks(entityId),
      );

      return Array.isArray(result) ? result : [];
    } catch (error) {
      throw CustomError.handleError(
        'AttendantsImpl.listEntityLinks -> error',
        error,
      );
    } finally {
      await this.logService.info('AttendantsImpl.listEntityLinks -> end');
    }
  }

  async syncEntityLinks(
    entityId: string,
    payload: SyncEntityAttendantsPayload,
  ): Promise<EntityAttendantLink[]> {
    try {
      await this.logService.info('AttendantsImpl.syncEntityLinks -> start');
      const selectedRootEntity = await this.assertRootEntityAccess();

      const result = await this.authService.http.put(
        ATTENDANT_ENDPOINTS.entityLinks(entityId),
        {
          ...payload,
          owner_root_entity_id:
            payload.owner_root_entity_id || selectedRootEntity.id,
        },
      );

      return Array.isArray(result) ? result : [];
    } catch (error) {
      throw CustomError.handleError(
        'AttendantsImpl.syncEntityLinks -> error',
        error,
      );
    } finally {
      await this.logService.info('AttendantsImpl.syncEntityLinks -> end');
    }
  }
}
